import {noop} from 'lodash';
import React, {FC} from 'react';
import {useDrop} from 'react-dnd';
import styled, {css} from 'styled-components';

import {hoverSelector} from '../../../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {boxShadow} from '../../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {SidePanelFrame} from '../../../../../store/stateless/sidePanelFrameSelectors';
import {SwitcherDragItemType} from '../draggableSwitcherItem';
import {FrameSwitcherItem} from '../frameSwitcherItem';

/*
 * Props.
 */

interface MultiViewButtonDropAreaProps {
  frame?: SidePanelFrame;
  isOpen: boolean;
  index: number;
  onDrop: (arg: string) => void;
  onFrameDragOver: (frameId: string, overFrameId: string) => void;
  onFrameDragStart: (arg: string) => void;
  onFrameDragEnd: (arg: string) => void;
}

/*
 * Style.
 */

interface StyledButtonDropAreaWrapperDivProps {
  $index: number;
  $isOpen: boolean;
  $hasSelectedFrame: boolean;
}

const StyledButtonDropAreaWrapperDiv = styled.div<StyledButtonDropAreaWrapperDivProps>`
  margin-top: 0;
  transition: margin-top 0.25s;
  border-radius: 50%;
  z-index: ${(p) => p.$index};

  ${(p) =>
    !p.$isOpen &&
    css`
      &:not(:first-child) {
        margin-top: -23px;
      }
    `}
  ${(p) => addSelectedFrameStyles(p.$hasSelectedFrame, p.$isOpen)}
`;

function addSelectedFrameStyles(hasSelectedFrame: boolean, isOpen: boolean) {
  if (isOpen || !hasSelectedFrame) {
    return '';
  }

  return css`
    ${(p) => boxShadow(p.theme, `0 0 0 2px ${p.theme.greys.shade20}`)};

    ${hoverSelector} {
      ${(p) => boxShadow(p.theme, `0 0 0 2px ${p.theme.greys.shade30}`)};
    }
  `;
}

interface StyledPlaceholderDivProps {
  $isOver: boolean;
  $isOpen: boolean;
}

const StyledPlaceholderDiv = styled.div<StyledPlaceholderDivProps>`
  height: 30px;
  width: 30px;
  border-radius: 100%;
  background: ${(p) => p.theme.greys.shade30};
  border: 1px dashed ${(p) => p.theme.greys.shade50};
  box-sizing: border-box;

  ${(p) =>
    !p.$isOpen &&
    css`
      ${hoverSelector} {
        background: ${p.theme.greys.shade40};
      }
    `};
  ${(p) =>
    p.$isOver &&
    css`
      border-color: ${p.theme.palette.blue.shade40};
    `};
`;

/*
 * Component.
 */

export const MultiViewButtonDropArea: FC<MultiViewButtonDropAreaProps> = (props) => {
  const {frame, isOpen, index, onDrop, onFrameDragOver, onFrameDragStart, onFrameDragEnd} = props;

  /*
   * Drag & Drop.
   */

  const [{isOver}, dropRef] = useDrop({
    accept: SwitcherDragItemType,
    drop: (item: any) => {
      onDrop(item.frameId);
    },
    collect: (monitor) => ({isOver: monitor.isOver()}),
  });

  return (
    <StyledButtonDropAreaWrapperDiv
      ref={dropRef}
      $isOpen={isOpen}
      $hasSelectedFrame={Boolean(frame)}
      $index={index}
    >
      {frame && (
        <FrameSwitcherItem
          selectedFrame={isOpen ? frame : undefined}
          frame={frame}
          // Index, isUserGlobalAdmin, and isOtherDragging are not needed.
          index={-1}
          isUserGlobalAdmin={false}
          isOtherDragging={false}
          onDragOver={onFrameDragOver}
          onDragStart={() => onFrameDragStart(frame.frameId)}
          onDragEnd={() => onFrameDragEnd(frame.frameId)}
          onFrameSelect={noop}
          isInMultiViewButton
        />
      )}
      {!frame && <StyledPlaceholderDiv $isOver={isOver} $isOpen={isOpen} />}
    </StyledButtonDropAreaWrapperDiv>
  );
};
